import { useEffect, useState } from 'react';
import { api, authHeaders, formatApiErrorDetail } from '@/lib/api';

const QUICK = [
  ['hero', 'Hero Campaigns'],
  ['artists', 'Artists'],
  ['videos', 'Videos'],
  ['content', 'Site Content'],
  ['settings', 'Social & Contact'],
];

export default function AdminDashboard({ token, user, onNavigate }) {
  const [stats, setStats] = useState({ artists: '—', campaigns: '—', videos: '—', submissions: '—' });
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, h, v, s] = await Promise.all([
          api.get('/artists'), api.get('/hero'), api.get('/videos'),
          api.get('/admin/submissions', { headers: authHeaders(token) }),
        ]);
        if (!alive) return;
        const heroCount = Array.isArray(h.data) ? h.data.length : h.data ? 1 : 0;
        setStats({
          artists: (a.data || []).length,
          campaigns: heroCount,
          videos: (v.data || []).length,
          submissions: (s.data || []).length,
        });
        setRecent((s.data || []).slice(0, 4));
      } catch (err) {
        if (alive) setError(formatApiErrorDetail(err.response?.data?.detail));
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const CARD = 'border border-brayo-line bg-white p-6 md:p-8';

  return (
    <div data-testid="admin-dashboard-panel">
      <p className="meta-label text-brayo-blue">Label Manager</p>
      <h2 className="display-title mt-3 text-3xl text-brayo-charcoal md:text-4xl">Welcome back{user?.email ? `, ${user.email}` : ''}</h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-brayo-mute md:text-base">
        Everything on the website is managed from here — the hero slider, the roster, videos, every line of copy, the socials and the store link.
      </p>

      {error && <p className="mt-6 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>}

      <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
        {[
          ['artists', 'Artists on roster'],
          ['campaigns', 'Hero campaigns'],
          ['videos', 'Videos'],
          ['submissions', 'Submissions'],
        ].map(([key, label]) => (
          <div key={key} data-testid={`admin-stat-${key}`} className={CARD}>
            <p className="display-title text-4xl text-brayo-blue md:text-5xl">{stats[key]}</p>
            <p className="meta-label mt-3 text-brayo-dim">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className={CARD}>
          <div className="flex items-center justify-between">
            <p className="meta-label text-brayo-blue">Recent Submissions</p>
            <button data-testid="admin-goto-submissions" onClick={() => onNavigate('submissions')} className="meta-label text-brayo-charcoal hover:text-brayo-blue">View All</button>
          </div>
          <ul className="mt-6 divide-y divide-brayo-line">
            {recent.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 py-3">
                <span className="truncate text-sm font-semibold text-brayo-charcoal">{s.name}</span>
                <span className="meta-label shrink-0 text-brayo-dim">{s.department} · {s.created_at?.slice(0, 10)}{s.unsubscribed ? ' · Unsubscribed' : ''}</span>
              </li>
            ))}
            {recent.length === 0 && <li className="py-3 text-sm text-brayo-dim">No submissions yet.</li>}
          </ul>
        </div>

        <div className={CARD}>
          <p className="meta-label text-brayo-blue">Quick Actions</p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {QUICK.map(([key, label]) => (
              <button
                key={key}
                data-testid={`admin-quick-${key}`}
                onClick={() => onNavigate(key)}
                className="meta-label border border-brayo-line px-4 py-3 text-brayo-charcoal transition-colors hover:border-brayo-blue hover:text-brayo-blue"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}